/**
 * THE canonical importance scale for user_preferences.*_importance columns.
 *
 *   1 = not important
 *   2 = nice to have
 *   3 = must have
 *   null = never answered (column omitted from the write)
 *
 * Every writer must emit only 1, 2, 3 or omit the column. Every reader must
 * treat null as "no answer" and must use strict `=== 3` for must-have gates.
 *
 * Scored columns: wifi, outlet, seating_comfort, parking, food are the columns
 * consumed by calculatePersonalizationScore. parking_importance is scored and
 * additionally drives getPersonalizedHeroAmenities.
 *
 * Interim note: rows written before this unification may still contain legacy
 * values (e.g. 5). Score math tolerates them; must-have gates ignore them.
 */

export const IMPORTANCE = {
  NOT: 1,
  NICE: 2,
  MUST: 3,
} as const;

export type ImportanceValue = 1 | 2 | 3;

export const IMPORTANCE_LABELS: Record<ImportanceValue, string> = {
  1: "Not important",
  2: "Nice to have",
  3: "Must have",
};

/** Ordered list of level values, low → high (used to render choice rows). */
export const IMPORTANCE_LEVELS: ImportanceValue[] = [
  IMPORTANCE.NOT,
  IMPORTANCE.NICE,
  IMPORTANCE.MUST,
];

export const IMPORTANCE_COLUMNS = [
  "wifi_importance",
  "outlet_importance",
  "seating_comfort_importance",
  "parking_importance",
  "food_importance",
] as const;

export type ImportanceColumn = (typeof IMPORTANCE_COLUMNS)[number];

/** Columns consumed by calculatePersonalizationScore. */
export const SCORED_IMPORTANCE_COLUMNS = [
  "wifi_importance",
  "outlet_importance",
  "seating_comfort_importance",
  "parking_importance",
  "food_importance",
] as const;

/**
 * Café-side parking tokens (cafe_amenities.parking_available, comma separated).
 * Single source of truth for how the personalization score reads parking.
 */
export const FREE_PARKING_TOKENS = [
  "free_lot",
  "lot",
  "free_garage",
  "free_street",
  "street",
] as const;

export const PAID_PARKING_TOKENS = [
  "paid_lot",
  "paid_garage",
  "garage",
  "metered",
  "paid_street",
] as const;

/** Partial credit (out of 10) awarded when parking exists but costs money. */
export const PARKING_PARTIAL_MULTIPLIER = 5;

export type ParkingClass = "free" | "paid" | "none" | "unknown";

/**
 * Classify a café's parking_available string. Best class wins, mirroring
 * getParkingGrade: any free token → free, else any paid token → paid, else an
 * explicit "none" → none, else unknown (empty, null, or unrecognised tokens).
 */
export function classifyParking(value?: string | null): ParkingClass {
  if (!value) return "unknown";
  const tokens = value
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return "unknown";
  if (tokens.some((t) => (FREE_PARKING_TOKENS as readonly string[]).includes(t))) {
    return "free";
  }
  if (tokens.some((t) => (PAID_PARKING_TOKENS as readonly string[]).includes(t))) {
    return "paid";
  }
  if (tokens.includes("none")) return "none";
  return "unknown";
}

export function isMustHave(value: number | null | undefined): boolean {
  return value === IMPORTANCE.MUST;
}

/**
 * Onboarding priorities screen → importance columns.
 * Picked tile = must have (3), unpicked tile = nice to have (2).
 * food_importance is never written by onboarding.
 */
export function prioritiesToImportance(
  priorities: string[]
): Record<string, ImportanceValue> {
  const has = (id: string) => priorities.includes(id);
  const level = (picked: boolean): ImportanceValue =>
    picked ? IMPORTANCE.MUST : IMPORTANCE.NICE;
  return {
    wifi_importance: level(has("wifi")),
    outlet_importance: level(has("outlets")),
    seating_comfort_importance: level(has("seating")),
    parking_importance: level(has("parking")),
  };
}

/**
 * Profile "work style" tiles → importance columns.
 * Selected tile = must have (3), unselected = nice to have (2).
 *
 * bandwidth → wifi, long_stay → outlets + seating, commuter → parking.
 * "full_day" no longer maps to importance (food is not written by any UI);
 * it is persisted only as a work_motivation.
 */
export function workStyleToImportance(
  motivations: string[]
): Record<string, ImportanceValue> {
  const has = (id: string) => motivations.includes(id);
  const level = (picked: boolean): ImportanceValue =>
    picked ? IMPORTANCE.MUST : IMPORTANCE.NICE;
  return {
    wifi_importance: level(has("bandwidth")),
    outlet_importance: level(has("long_stay")),
    seating_comfort_importance: level(has("long_stay")),
    parking_importance: level(has("commuter")),
  };
}
